import PropTypes from 'prop-types';
import { action, extendObservable } from 'mobx';
import { PolicyModalSectionProps } from '../PolicyModalStore';
import RadioButtonStore from '../../RadioButtonStore';
import FuseSelectStore from '../../FuseSelectStore';
import AsyncActionStore from '../../AsyncActionStore';
import debug from '../../../debug';
import FlagStore from '../../FlagStore';
import { RULE_REC_TYPE } from '../../../constants';

const logger = debug.create('AdditionalConditionsStore', true);

const DEFAULT_VALUES = {
    blockMalware: true,
    blockOnTimeout: true,
    blockOnDetection: true,
    blockUnsupportedFiles: true,
    detectorSets: { items: [] }
};

export default class AdditionalConditionsStore {
    static PropType = PropTypes.shape({
        blockMalware: RadioButtonStore.PropType.isRequired,
        blockOnTimeout: RadioButtonStore.PropType.isRequired,
        blockOnDetection: RadioButtonStore.PropType.isRequired,
        blockUnsupportedFiles: RadioButtonStore.PropType.isRequired,
        detectorSetsSelect: FuseSelectStore.PropType.isRequired,
        shouldSkipValidation: FlagStore.PropType.isRequired,

        ...PolicyModalSectionProps,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class AdditionalConditionsStore */{
            apiGetDetectorSets: null,
            apiGetDetectorSetsStatus: new AsyncActionStore(),
            blockMalware: new RadioButtonStore(),
            blockOnTimeout: new RadioButtonStore(),
            blockUnsupportedFiles: new RadioButtonStore(),
            blockOnDetection: new RadioButtonStore(),
            detectorSetsSelect: new FuseSelectStore(),
            shouldSkipValidation: new FlagStore(false),
            allDetectorSetsById: {},
            hasDeletedDetectorSet: false,
            originalValue: {},
            ruleType: null,
        }, data);
    }

    convertToFuseSelect = (ds) => ({
        value: ds.id,
        label: ds.name,
    })

    convertFromFuseSelect = (op) => ({
        id: op.value,
        name: op.label
    });

    hashById = (arr) =>
        arr.reduce((acc, item) => {
            acc[item.id] = this.convertToFuseSelect(item);
            return acc;
        }, {});

    preload = () => {
        if (!this.apiGetDetectorSetsStatus.isLoading) {
            this.apiGetDetectorSetsStatus.setLoading(this.apiGetDetectorSets().then(action((res) => {
                const options = res.items.map(this.convertToFuseSelect);
                this.allDetectorSetsById = this.hashById(res.items || []);
                this.detectorSetsSelect.setOptions(options);
            })).catch((reason) => logger.error(reason)));
        }
        return this.apiGetDetectorSetsStatus.promise;
    }

    isDetectorsLoadedEmpty = () => (Object.values(this.allDetectorSetsById).length === 0
        && !this.apiGetDetectorSetsStatus.isLoading)

    markDeletedDetectorSets = (ds) => {
        const deletedDetectorSet = ds.filter((d) => !this.allDetectorSetsById[d.id]).map((d) => ({
            error: true,
            value: d.id,
            label: d.name + ' (deleted)',
        })) || [];
        if (deletedDetectorSet.length > 0) {
            this.hasDeletedDetectorSet = true;
            this.detectorSetsSelect.setOptions([
                ...Object.values(this.allDetectorSetsById),
                ...deletedDetectorSet
            ]);
        }
    }

    getDefaultValues = () => (this.ruleType === RULE_REC_TYPE.FILE_THREAT_RULES ? {
        ...DEFAULT_VALUES,
        blockUnsupportedFiles: false
    } : DEFAULT_VALUES);

    setValue = action((value) => {
        try {
            value = { ...this.getDefaultValues(), ...value };
            const items = value.detectorSets?.items?.map((ds) => ds.id) || [];
            this.hasDeletedDetectorSet = false;
            if (items.length > 0) {
                this.markDeletedDetectorSets(value.detectorSets?.items || []);
            }
            this.originalValue = value;
            this.originalValue.detectorSets = { items };
            this.blockMalware.setValue(value.blockMalware);
            this.blockOnTimeout.setValue(value.blockOnTimeout);
            this.blockOnDetection.setValue(value.blockOnDetection);
            this.blockUnsupportedFiles.setValue(value.blockUnsupportedFiles);
            this.detectorSetsSelect.setSelectedValues([...items]);
        } catch (e) {
            logger.error('error setting value', e.message);
        }
    });

    getValue = () => ({
        blockMalware: this.blockMalware.getValue(),
        blockOnTimeout: this.blockOnTimeout.getValue(),
        blockOnDetection: this.blockOnDetection.getValue(),
        blockUnsupportedFiles: this.blockUnsupportedFiles.getValue(),
        detectorSets: { items: this.detectorSetsSelect.getSelectedItems().map(this.convertFromFuseSelect)
        },
    });

    hasChanged = () => this.originalValue.blockMalware !== this.blockMalware.getValue() ||
        this.originalValue.blockOnTimeout !== this.blockOnTimeout.getValue() ||
        this.originalValue.blockOnDetection !== this.blockOnDetection.getValue() ||
        this.originalValue.blockUnsupportedFiles !== this.blockUnsupportedFiles.getValue() ||
        FuseSelectStore.haveDifferentFlatItems(this.originalValue.detectorSets.items,
            this.detectorSetsSelect.selectedValues);

    isValid = () => (this.shouldSkipValidation.getValue() || this.detectorSetsSelect.selectedValues.length !== 0);
}
